import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { sessionsApi } from '@api/endpoints/sessions'
import { adventuresApi } from '@api/endpoints/adventures'
import { useSessionStore } from '../stores/sessionStore'
import {
  SessionStatus,
  SessionListDto,
  SessionStateDto,
  CreateSessionRequest,
  InitiateBootVoteRequest,
  CastBootVoteRequest,
  MarkJournalReadRequest,
  UpdateSessionSettingsRequest,
} from '@api/types/session'

// Query keys
export const sessionKeys = {
  all: ['sessions'] as const,
  active: () => [...sessionKeys.all, 'active'] as const,
  forAdventure: (adventureId: string) => [...sessionKeys.all, 'adventure', adventureId] as const,
  state: (sessionId: string) => [...sessionKeys.all, 'state', sessionId] as const,
  journal: (sessionId: string) => [...sessionKeys.all, 'journal', sessionId] as const,
  voting: (sessionId: string, roundId: string) => [...sessionKeys.all, 'voting', sessionId, roundId] as const,
  invites: (sessionId: string) => [...sessionKeys.all, 'invites', sessionId] as const,
  participants: (sessionId: string) => [...sessionKeys.all, 'participants', sessionId] as const,
  bootVotes: (sessionId: string) => [...sessionKeys.all, 'bootVotes', sessionId] as const,
  validateStart: (sessionId: string) => [...sessionKeys.all, 'validateStart', sessionId] as const,
  moderatorStatus: (adventureId: string) => [...sessionKeys.all, 'moderatorStatus', adventureId] as const,
}

interface ActiveSessionOptions {
  enabled?: boolean
}

/**
 * Check for existing active session for a specific adventure
 * Used to determine if user should resume or create new session
 */
export function useActiveSessionForAdventure(
  adventureId: string | undefined,
  options?: ActiveSessionOptions
) {
  const enabledOption = options?.enabled ?? true

  return useQuery({
    queryKey: sessionKeys.forAdventure(adventureId!),
    queryFn: async () => {
      try {
        const result = await sessionsApi.getActiveSessionForAdventure(adventureId!)
        return result as SessionListDto | null
      } catch (error: any) {
        // 404 means no active session - that's expected, not an error
        // 500 is a backend bug - treat as no session rather than blocking the page
        const status = error?.response?.status || error?.status
        if (status === 404 || status === 500) {
          return null
        }
        throw error
      }
    },
    enabled: !!adventureId && enabledOption,
    staleTime: 30000, // 30 seconds
    retry: 1, // Don't retry excessively - prevents blocking page load
  })
}

/**
 * Get current session state with optional polling
 * Used for keeping UI in sync with backend state
 */
export function useSessionState(
  sessionId: string | null,
  options?: {
    pollInterval?: number
    enabled?: boolean
  }
) {
  const store = useSessionStore()

  return useQuery({
    queryKey: sessionKeys.state(sessionId!),
    queryFn: async () => {
      const state = await sessionsApi.getSessionState(sessionId!)
      // Store raw API response for debugging (before any processing)
      store.setLastStateResponse(state)
      // Update store with latest state
      store.updateSessionState(state)
      // Capture completion summary if present (terminal state reached)
      if (state.completionSummary) {
        store.setCompletionSummary(state.completionSummary)
      }
      return state as SessionStateDto
    },
    enabled: !!sessionId && options?.enabled !== false,
    refetchInterval: options?.pollInterval,
    refetchIntervalInBackground: false,
    staleTime: 5000, // 5 seconds
  })
}

/**
 * Create a new session for an adventure
 */
export function useCreateSession() {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  return useMutation({
    mutationFn: async (data: CreateSessionRequest) => {
      store.setLoading(true)
      store.setError(null)
      const result = await sessionsApi.createSession(data)
      return result
    },
    onSuccess: (result, variables) => {
      // Invalidate existing sessions query
      queryClient.invalidateQueries({ queryKey: sessionKeys.forAdventure(variables.adventureId) })
      queryClient.invalidateQueries({ queryKey: sessionKeys.active() })
      store.setLoading(false)
    },
    onError: (error) => {
      store.setLoading(false)
      store.setError(error instanceof Error ? error.message : 'Failed to create session')
    },
  })
}

/**
 * Start a session (transition from NotStarted to InProgress)
 */
export function useStartSession() {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      store.setLoading(true)
      store.setError(null)
      const result = await sessionsApi.startSession(sessionId)
      return { result, sessionId }
    },
    onSuccess: async ({ result, sessionId }) => {
      // Handle wrapped response (result.data) or direct response
      const sessionState = result?.data || result

      if (sessionState && (sessionState as any).sessionId) {
        store.loadSession(sessionState as any)
      } else {
        // If mutation result doesn't include session data, fetch it directly
        try {
          const state = await sessionsApi.getSessionState(sessionId)
          store.loadSession(state as any)
        } catch (err) {
          console.error('Failed to fetch session state after start:', err)
          store.setError('Session started but failed to load state')
        }
      }
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
      queryClient.invalidateQueries({ queryKey: sessionKeys.journal(sessionId) })
      store.setLoading(false)
    },
    onError: (error) => {
      store.setLoading(false)
      store.setError(error instanceof Error ? error.message : 'Failed to start session')
    },
  })
}

/**
 * Resume a paused session
 */
export function useResumeSession() {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      store.setLoading(true)
      store.setError(null)
      const result = await sessionsApi.resumeSession(sessionId)
      return { result, sessionId }
    },
    onSuccess: async ({ result, sessionId }) => {
      // Handle wrapped response (result.data) or direct response
      const sessionState = result?.data || result

      if (sessionState && (sessionState as any).sessionId) {
        store.loadSession(sessionState as any)
      } else {
        // If mutation result doesn't include session data, fetch it directly
        try {
          const state = await sessionsApi.getSessionState(sessionId)
          store.loadSession(state as any)
        } catch (err) {
          console.error('Failed to fetch session state after resume:', err)
          store.setError('Session resumed but failed to load state')
        }
      }
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
      queryClient.invalidateQueries({ queryKey: sessionKeys.journal(sessionId) })
      store.setLoading(false)
    },
    onError: (error) => {
      store.setLoading(false)
      store.setError(error instanceof Error ? error.message : 'Failed to resume session')
    },
  })
}

/**
 * Process session (auto-advance through auto-advance nodes)
 */
export function useProcessSession() {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      store.setProcessing(true)
      store.setError(null)
      const result = await sessionsApi.processSession(sessionId)
      return result
    },
    onSuccess: (result, sessionId) => {
      store.setProcessing(false)
      if (result.data) {
        // Update store with new session state
        store.updateSessionState(result.data.session)
        // Add any new journal entries
        if (result.data.newJournalEntries?.length > 0) {
          store.addJournalEntries(result.data.newJournalEntries)
        }
        // Capture completion summary if present (terminal state reached)
        if (result.data.completionSummary) {
          store.setCompletionSummary(result.data.completionSummary)
        }
      }
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
      // Also invalidate journal to fetch any new entries from the server
      queryClient.invalidateQueries({ queryKey: sessionKeys.journal(sessionId) })
    },
    onError: (error) => {
      store.setProcessing(false)
      store.setError(error instanceof Error ? error.message : 'Failed to process session')
    },
  })
}

/**
 * Pause session
 */
export function usePauseSession() {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const result = await sessionsApi.pauseSession(sessionId)
      return result
    },
    onSuccess: (_, sessionId) => {
      store.setStatus(SessionStatus.Paused)
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
    },
    onError: (error) => {
      store.setError(error instanceof Error ? error.message : 'Failed to pause session')
    },
  })
}

/**
 * Abandon session (quit the adventure)
 */
export function useAbandonSession() {
  const queryClient = useQueryClient()
  const store = useSessionStore()

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const result = await sessionsApi.abandonSession(sessionId)
      return result
    },
    onSuccess: (result) => {
      // Capture completion summary from abandon response and show exit screen
      if (result?.data?.completionSummary) {
        store.setCompletionSummary(result.data.completionSummary)
      }
      store.setStatus(SessionStatus.Abandoned)
      // Invalidate all session queries
      queryClient.invalidateQueries({ queryKey: sessionKeys.all })
    },
    onError: (error) => {
      store.setError(error instanceof Error ? error.message : 'Failed to abandon session')
    },
  })
}

/**
 * Create a group session (Public or InviteOnly) via POST /api/sessions
 * Returns AdventureSessionFullDto with conversationId and participants
 */
export function useCreateGroupSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (data: CreateSessionRequest) => {
      const result = await sessionsApi.createSession(data)
      return result
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.forAdventure(variables.adventureId) })
      queryClient.invalidateQueries({ queryKey: sessionKeys.active() })
    },
  })
}

// ========================
// Ready / Kick / Boot Vote Hooks
// ========================

/**
 * Mark self as ready/unready in a group session
 */
export function useMarkReady() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ sessionId, ready = true }: { sessionId: string; ready?: boolean }) => {
      return sessionsApi.markReady(sessionId, ready)
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.participants(sessionId) })
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
    },
  })
}

/**
 * Kick a participant from a group session (owner only)
 */
export function useKickParticipant() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ sessionId, targetUserId, reason }: { sessionId: string; targetUserId: string; reason?: string }) => {
      return sessionsApi.kickParticipant(sessionId, targetUserId, reason)
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.participants(sessionId) })
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
    },
  })
}

/**
 * Initiate a boot vote against a participant
 */
export function useInitiateBootVote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ sessionId, data }: { sessionId: string; data: InitiateBootVoteRequest }) => {
      return sessionsApi.initiateBootVote(sessionId, data)
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.bootVotes(sessionId) })
    },
  })
}

/**
 * Cast a vote in an active boot vote
 */
export function useCastBootVote() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ sessionId, voteId, data }: { sessionId: string; voteId: string; data: CastBootVoteRequest }) => {
      return sessionsApi.castBootVote(sessionId, voteId, data)
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.bootVotes(sessionId) })
      queryClient.invalidateQueries({ queryKey: sessionKeys.participants(sessionId) })
    },
  })
}

/**
 * Get active boot votes for a session (polls every 10s when enabled)
 */
export function useActiveBootVotes(sessionId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: sessionKeys.bootVotes(sessionId!),
    queryFn: () => sessionsApi.getActiveBootVotes(sessionId!),
    enabled: !!sessionId && options?.enabled !== false,
    refetchInterval: 10000,
    refetchIntervalInBackground: false,
  })
}

/**
 * Mark journal entries as read up to a sequence number
 */
export function useMarkJournalRead() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ sessionId, data }: { sessionId: string; data: MarkJournalReadRequest }) => {
      return sessionsApi.markJournalRead(sessionId, data)
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
    },
  })
}

/**
 * Validate session can be started (on-demand, not auto-polling)
 */
export function useValidateSessionStart(sessionId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: sessionKeys.validateStart(sessionId!),
    queryFn: () => sessionsApi.validateSessionStart(sessionId!),
    enabled: !!sessionId && options?.enabled === true,
    staleTime: 10000,
  })
}

/**
 * Get moderator online status for an adventure (polls every 30s)
 */
export function useModeratorStatus(adventureId: string | null, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: sessionKeys.moderatorStatus(adventureId!),
    queryFn: () => adventuresApi.getModeratorStatus(adventureId!),
    enabled: !!adventureId && options?.enabled !== false,
    refetchInterval: 30000,
    refetchIntervalInBackground: false,
  })
}

/**
 * Update session settings (owner only)
 * Invalidates session state to pick up the updated votingSettings
 */
export function useUpdateSessionSettings(sessionId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (data: UpdateSessionSettingsRequest) =>
      sessionsApi.updateSessionSettings(sessionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.state(sessionId) })
    },
  })
}

/**
 * Leave a group session
 */
export function useLeaveSession() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (sessionId: string) => sessionsApi.leaveSession(sessionId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: sessionKeys.all })
    },
  })
}

